import { z } from 'zod';
import { Id } from '../primitives.js';
import { PaymentMethodView, SubscriptionState } from './billing.js';

/**
 * Платформенная админка: кросс-тенантный обзор подписок для сотрудников
 * Odonix, а не для клиники. Схемы отдельные от `billing.ts`, а не расширение
 * его же: там — то, что видит владелец клиники о своей подписке, здесь — то,
 * что видит оператор платформы о чужой. Общее поведение (состояние, тариф)
 * переиспользуется импортом, а не копированием — они обязаны остаться
 * буквально одним и тем же перечислением.
 *
 * Модуль: docs/03-modules/20-platform-admin.md
 * Решение: docs/02-architecture/adr/ADR-0011-platform-admin-service.md
 */

// --- Вход -------------------------------------------------------------------

export const AdminLoginRequest = z.object({
  email: z.string().min(1),
  password: z.string().min(1),
});
export type AdminLoginRequest = z.infer<typeof AdminLoginRequest>;

export const AdminMfaVerifyRequest = z.object({
  challengeToken: z.string().min(1),
  code: z.string().min(1),
});
export type AdminMfaVerifyRequest = z.infer<typeof AdminMfaVerifyRequest>;

export const AdminAuthenticatedResult = z.object({
  status: z.literal('authenticated'),
  accessToken: z.string(),
  staff: z.object({ id: Id, email: z.string(), fullName: z.string().nullable() }),
});
export type AdminAuthenticatedResult = z.infer<typeof AdminAuthenticatedResult>;

export const AdminMfaRequiredResult = z.object({
  status: z.literal('mfa_required'),
  challengeToken: z.string(),
});
export type AdminMfaRequiredResult = z.infer<typeof AdminMfaRequiredResult>;

export const AdminLoginResult = z.discriminatedUnion('status', [
  AdminAuthenticatedResult,
  AdminMfaRequiredResult,
]);
export type AdminLoginResult = z.infer<typeof AdminLoginResult>;

export const AdminMeResponse = z.object({
  id: Id,
  email: z.string(),
  fullName: z.string().nullable(),
});
export type AdminMeResponse = z.infer<typeof AdminMeResponse>;

// --- Клиники ------------------------------------------------------------------

/**
 * Ровно те поля, что зафиксированы областью первой версии: биллинг и базовый
 * профиль. Ни одного медицинского поля здесь нет и не может быть — их не
 * знает ни один из двух источников, из которых собирается этот вид: схема
 * `billing` и `GET /internal/tenants` ядра.
 */
export const AdminTenantSummary = z.object({
  id: Id,
  kind: z.enum(['clinic', 'lab']),
  name: z.string(),
  createdAt: z.iso.datetime(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  ownerPhone: z.string().nullable(),
  subscriptionState: SubscriptionState,
  currentPeriodEnd: z.iso.date().nullable(),
  trialEndsOn: z.iso.date().nullable(),
  /** За кем из менеджеров клиника сейчас; `null` — ни за кем. */
  manager: z.object({ id: Id, fullName: z.string() }).nullable(),
});
export type AdminTenantSummary = z.infer<typeof AdminTenantSummary>;

export const AdminTenantListResponse = z.object({
  tenants: z.array(AdminTenantSummary),
  nextCursor: z.string().nullable(),
});
export type AdminTenantListResponse = z.infer<typeof AdminTenantListResponse>;

export const AdminTenantListQuery = z.object({
  q: z.string().max(200).optional(),
  state: SubscriptionState.optional(),
  kind: z.enum(['clinic', 'lab']).optional(),
  /** Клиники одного менеджера; `none` — ни за кем не закреплённые. */
  managerId: z.union([Id, z.literal('none')]).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AdminTenantListQuery = z.infer<typeof AdminTenantListQuery>;

export const AdminPaymentView = z.object({
  id: Id,
  kind: z.enum(['initial', 'recurring', 'manual']),
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  amountMinor: z.number().int(),
  status: z.enum(['pending', 'succeeded', 'canceled', 'failed']),
  failureReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  settledAt: z.iso.datetime().nullable(),
});
export type AdminPaymentView = z.infer<typeof AdminPaymentView>;

export const AdminTenantDetail = z.object({
  tenant: AdminTenantSummary,
  plan: z.object({ code: z.string(), name: z.string(), priceMinor: z.number().int(), currency: z.string() }),
  attemptsMade: z.number().int(),
  lastFailure: z.string().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelReason: z.string().nullable(),
  restrictedAt: z.iso.datetime().nullable(),
  method: PaymentMethodView.nullable(),
  payments: z.array(AdminPaymentView),
  /** Менеджер клиники: с какого момента и как закреплён. */
  manager: z
    .object({
      id: Id,
      fullName: z.string(),
      since: z.iso.datetime(),
      source: z.enum(['referral', 'staff']),
    })
    .nullable(),
});
export type AdminTenantDetail = z.infer<typeof AdminTenantDetail>;

/** Закрепить клинику за менеджером или снять с неё менеджера (`null`). */
export const AssignTenantManagerRequest = z.object({
  managerId: Id.nullable(),
});
export type AssignTenantManagerRequest = z.infer<typeof AssignTenantManagerRequest>;

// --- Действия -----------------------------------------------------------------
//
// Каждое действие обязано нести причину: она уходит и в аудит платформенной
// админки, и в аудит-лог самой подписки (см. docs/03-modules/20-platform-admin.md
// §7). Пустая причина отклоняется на границе контракта, а не соглашением
// экрана — форма без заполненной причины не имеет права отправить запрос.

const Reason = z.string().trim().min(3, 'Укажите причину действия').max(500);

export const ExtendTrialRequest = z.object({
  newTrialEndsOn: z.iso.date(),
  reason: Reason,
});
export type ExtendTrialRequest = z.infer<typeof ExtendTrialRequest>;

export const ActivateRequest = z.object({
  reason: Reason,
});
export type ActivateRequest = z.infer<typeof ActivateRequest>;

export const ManualPaymentRequest = z.object({
  amountMinor: z.number().int().positive(),
  periodDays: z.number().int().min(1).max(366).default(30),
  reason: Reason,
});
export type ManualPaymentRequest = z.infer<typeof ManualPaymentRequest>;

export const BlockRequest = z.object({
  reason: Reason,
});
export type BlockRequest = z.infer<typeof BlockRequest>;

export const RestrictRequest = z.object({
  reason: Reason,
});
export type RestrictRequest = z.infer<typeof RestrictRequest>;

export const AdminActionResult = z.object({
  tenantId: Id,
  state: SubscriptionState,
});
export type AdminActionResult = z.infer<typeof AdminActionResult>;

// --- Аудит платформенной админки -----------------------------------------------

export const AdminAuditAction = z.enum([
  'extend_trial',
  'activate',
  'manual_payment',
  'block',
  'restrict',
]);
export type AdminAuditAction = z.infer<typeof AdminAuditAction>;

export const AdminAuditEntry = z.object({
  id: Id,
  staffEmail: z.string(),
  action: AdminAuditAction,
  targetTenantId: Id,
  targetTenantName: z.string().nullable(),
  reason: z.string(),
  meta: z.record(z.string(), z.unknown()),
  occurredAt: z.iso.datetime(),
});
export type AdminAuditEntry = z.infer<typeof AdminAuditEntry>;

export const AdminAuditListQuery = z.object({
  tenantId: Id.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AdminAuditListQuery = z.infer<typeof AdminAuditListQuery>;

export const AdminAuditListResponse = z.object({
  entries: z.array(AdminAuditEntry),
  nextCursor: z.string().nullable(),
});
export type AdminAuditListResponse = z.infer<typeof AdminAuditListResponse>;

// --- Дашборд --------------------------------------------------------------
//
// Заработок платформы и состояние базы арендаторов одним экраном. Числа
// собраны из тех же двух источников, что список и карточка (§ выше), а не из
// отдельной read-модели: на масштабе одной платформы пересчёт при каждом
// открытии экрана дешевле, чем вторая копия тех же данных, которая может
// разойтись с первой.

export const AdminRevenueMonth = z.object({
  /** Первый день месяца, `YYYY-MM-DD`. */
  month: z.iso.date(),
  amountMinor: z.number().int(),
});
export type AdminRevenueMonth = z.infer<typeof AdminRevenueMonth>;

export const AdminDashboardResponse = z.object({
  /** Сумма цены тарифа по всем подпискам в `active`/`grace` — то, что платформа
   *  ожидает получить в текущем цикле, а не то, что уже поступило. */
  mrrMinor: z.number().int(),
  tenantsByState: z.record(SubscriptionState, z.number().int()),
  tenantsByKind: z.object({ clinic: z.number().int(), lab: z.number().int() }),
  /** Зарегистрированы в текущем календарном месяце (московское время). */
  newTenantsThisMonth: z.number().int(),
  /** Пробный период кончается в ближайшие 3 дня — требует внимания раньше,
   *  чем клиника напомнит о себе сама. */
  trialEndingSoon: z.number().int(),
  /** Поступившая оплата по месяцам, последние месяцы включая текущий,
   *  от старого к новому. */
  revenueByMonth: z.array(AdminRevenueMonth),
});
export type AdminDashboardResponse = z.infer<typeof AdminDashboardResponse>;

// --- Восстановление пароля сотрудника платформы ----------------------------------
//
// Ссылка уходит на почту сотрудника и меняет только пароль: второй фактор
// остаётся прежним, и вход после смены всё равно спросит код из
// приложения-аутентификатора. Ответ на запрос один и тот же, есть такой адрес
// или нет.

export const AdminPasswordResetRequest = z.object({
  email: z.email().max(320),
});
export type AdminPasswordResetRequest = z.infer<typeof AdminPasswordResetRequest>;

export const AdminPasswordResetConfirm = z.object({
  token: z.string().min(40).max(200),
  /** Длиннее, чем у сотрудника клиники: аккаунт меняет деньги всей платформы. */
  password: z.string().min(12, 'Пароль не короче 12 символов').max(200),
});
export type AdminPasswordResetConfirm = z.infer<typeof AdminPasswordResetConfirm>;

// --- Менеджеры --------------------------------------------------------------------
//
// Менеджер Odonix ведёт клиники и лаборатории: видит контакты и подписки,
// звонит, напоминает об оплате. Заводит, переименовывает и отключает его
// сотрудник платформы; пароля у менеджера нет — вход по шестизначному коду на
// почту. Денег менеджер не трогает: ни продления, ни блокировки ему не
// доступны, это остаётся действиями платформенной админки.

/** `deleted` — удалён сотрудником платформы: войти не может, в списках не
 *  виден, но его начисления и журнал остаются — это история денег. */
export const ManagerStatus = z.enum(['active', 'disabled', 'deleted']);
export type ManagerStatus = z.infer<typeof ManagerStatus>;

/** Что сотрудник платформы может поставить правкой: удаление — отдельным
 *  действием с решением о клиниках менеджера. */
export const ManagerSettableStatus = z.enum(['active', 'disabled']);
export type ManagerSettableStatus = z.infer<typeof ManagerSettableStatus>;

export const ManagerView = z.object({
  id: Id,
  email: z.string(),
  fullName: z.string(),
  phone: z.string().nullable(),
  status: ManagerStatus,
  /** Действующий процент от оплат подписки его клиник. */
  commissionPercent: z.number(),
  createdAt: z.iso.datetime(),
  lastLoginAt: z.iso.datetime().nullable(),
  disabledAt: z.iso.datetime().nullable(),
  deletedAt: z.iso.datetime().nullable(),
});
export type ManagerView = z.infer<typeof ManagerView>;

export const ManagerListResponse = z.object({
  managers: z.array(ManagerView),
});
export type ManagerListResponse = z.infer<typeof ManagerListResponse>;

const ManagerName = z.string().trim().min(2, 'Имя не короче двух букв').max(200);

/** Пусто — телефона нет; иначе цифры с необязательным плюсом, скобками,
 *  дефисами и пробелами — так, как его записали. */
const ManagerPhone = z
  .string()
  .trim()
  .max(32)
  .refine((value) => value === '' || /^\+?[\d\s()-]{10,20}$/.test(value), 'Телефон: от 10 цифр, можно с плюсом');

/** Процент с точностью до сотых: 10, 7,5, 12,25. */
export const CommissionPercent = z
  .number()
  .min(0, 'Не меньше нуля')
  .max(100, 'Не больше 100 %')
  .refine((value) => Math.abs(Math.round(value * 100) - value * 100) < 1e-6, 'Не точнее сотых');

export const CreateManagerRequest = z.object({
  email: z.email().max(320),
  fullName: ManagerName,
  phone: ManagerPhone.optional(),
  commissionPercent: CommissionPercent.optional(),
});
export type CreateManagerRequest = z.infer<typeof CreateManagerRequest>;

/** `invitationDelivered: false` — письмо со ссылкой на кабинет не ушло, и
 *  ссылку менеджеру стоит передать другим путём. */
export const CreateManagerResponse = z.object({
  manager: ManagerView,
  invitationDelivered: z.boolean(),
});
export type CreateManagerResponse = z.infer<typeof CreateManagerResponse>;

export const UpdateManagerRequest = z
  .object({
    fullName: ManagerName.optional(),
    /** Новая почта — новый адрес для кода входа; открытые сессии живут. */
    email: z.email().max(320).optional(),
    /** Пустая строка стирает телефон. */
    phone: ManagerPhone.optional(),
    status: ManagerSettableStatus.optional(),
  })
  .refine(
    (value) =>
      value.fullName !== undefined || value.email !== undefined || value.phone !== undefined || value.status !== undefined,
    { message: 'Укажите, что изменить' },
  );
export type UpdateManagerRequest = z.infer<typeof UpdateManagerRequest>;

/**
 * Что пишется в журнал менеджеров. Действия самого менеджера — вход, карточки,
 * звонки и письма; действия сотрудника платформы над менеджером — тем же
 * журналом, чтобы история одного человека читалась целиком.
 */
export const ManagerAuditAction = z.enum([
  'code_requested',
  'login',
  'login_failed',
  'logout',
  'tenant_viewed',
  'tenant_called',
  'tenant_emailed',
  'manager_created',
  'manager_updated',
  'manager_disabled',
  'manager_enabled',
  'manager_deleted',
  'commission_changed',
  'bonus_added',
  'bonus_cancelled',
  /** Клиника закреплена: по ссылке при регистрации, сотрудником или передачей
   *  от удалённого менеджера (`meta.source`). */
  'tenant_assigned',
  'tenant_unassigned',
  'link_created',
  'link_updated',
  'link_disabled',
  'link_enabled',
]);
export type ManagerAuditAction = z.infer<typeof ManagerAuditAction>;

export const ManagerAuditEntry = z.object({
  id: Id,
  managerId: Id,
  managerEmail: z.string(),
  managerName: z.string().nullable(),
  action: ManagerAuditAction,
  targetTenantId: Id.nullable(),
  targetTenantName: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
  occurredAt: z.iso.datetime(),
});
export type ManagerAuditEntry = z.infer<typeof ManagerAuditEntry>;

export const ManagerAuditListQuery = z.object({
  managerId: Id.optional(),
  action: ManagerAuditAction.optional(),
  tenantId: Id.optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ManagerAuditListQuery = z.infer<typeof ManagerAuditListQuery>;

export const ManagerAuditListResponse = z.object({
  entries: z.array(ManagerAuditEntry),
  nextCursor: z.string().nullable(),
});
export type ManagerAuditListResponse = z.infer<typeof ManagerAuditListResponse>;

// --- Вход менеджера ------------------------------------------------------------------

export const MANAGER_CODE_LENGTH = 6;

export const ManagerCodeRequest = z.object({
  email: z.email().max(320),
});
export type ManagerCodeRequest = z.infer<typeof ManagerCodeRequest>;

/** Ответ одинаков для любого адреса: по нему не узнать, заведён ли менеджер. */
export const ManagerCodeRequested = z.object({
  expiresInSec: z.number().int(),
  resendAfterSec: z.number().int(),
});
export type ManagerCodeRequested = z.infer<typeof ManagerCodeRequested>;

export const ManagerCodeVerifyRequest = z.object({
  email: z.email().max(320),
  code: z.string().regex(/^\d{6}$/, 'Код — шесть цифр'),
});
export type ManagerCodeVerifyRequest = z.infer<typeof ManagerCodeVerifyRequest>;

export const ManagerMeResponse = z.object({
  id: Id,
  email: z.string(),
  fullName: z.string(),
});
export type ManagerMeResponse = z.infer<typeof ManagerMeResponse>;

export const ManagerAuthenticatedResult = z.object({
  accessToken: z.string(),
  manager: ManagerMeResponse,
});
export type ManagerAuthenticatedResult = z.infer<typeof ManagerAuthenticatedResult>;

// --- Клиники и лаборатории глазами менеджера ---------------------------------------

export const TenantKindFilter = z.enum(['clinic', 'lab']);

export const ManagerTenantSort = z.enum(['ending', 'newest', 'name']);
export type ManagerTenantSort = z.infer<typeof ManagerTenantSort>;

export const ManagerTenantListQuery = z.object({
  q: z.string().max(200).optional(),
  kind: TenantKindFilter.optional(),
  state: SubscriptionState.optional(),
  /** Подписка или пробный период кончаются не позже чем через столько дней
   *  (включая уже закончившиеся). */
  endingWithinDays: z.coerce.number().int().min(1).max(90).optional(),
  sort: ManagerTenantSort.default('ending'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ManagerTenantListQuery = z.infer<typeof ManagerTenantListQuery>;

/**
 * Строка списка. Контакты — двумя парами: клиники (из её реквизитов) и
 * владельца (из учётной записи). Звонят обычно в клинику, а о деньгах говорят
 * с владельцем — нужны обе.
 */
export const ManagerTenantSummary = z.object({
  id: Id,
  kind: TenantKindFilter,
  name: z.string(),
  createdAt: z.iso.datetime(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  ownerName: z.string().nullable(),
  ownerPhone: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  subscriptionState: SubscriptionState,
  planCode: z.string().nullable(),
  /** К этой дате доступ «подходит к концу»: конец пробного периода или
   *  оплаченного. `null` — даты нет (подписка ещё не заведена). */
  endsOn: z.iso.date().nullable(),
  /** Дней до `endsOn` по Москве; отрицательное — дата уже прошла. */
  daysLeft: z.number().int().nullable(),
});
export type ManagerTenantSummary = z.infer<typeof ManagerTenantSummary>;

/** Счётчики для переключателей — по всей базе с учётом поиска, но без учёта
 *  выбранных вида и состояния: иначе выбранный фильтр обнулял бы соседние. */
export const ManagerTenantCounts = z.object({
  all: z.number().int(),
  clinic: z.number().int(),
  lab: z.number().int(),
  /** Пробный период или оплата кончаются в ближайшие 7 дней или уже кончились. */
  endingSoon: z.number().int(),
  byState: z.record(SubscriptionState, z.number().int()),
});
export type ManagerTenantCounts = z.infer<typeof ManagerTenantCounts>;

export const ManagerTenantListResponse = z.object({
  tenants: z.array(ManagerTenantSummary),
  nextCursor: z.string().nullable(),
  counts: ManagerTenantCounts,
});
export type ManagerTenantListResponse = z.infer<typeof ManagerTenantListResponse>;

export const ManagerTenantDetail = z.object({
  tenant: ManagerTenantSummary,
  legalName: z.string().nullable(),
  inn: z.string().nullable(),
  website: z.string().nullable(),
  directorName: z.string().nullable(),
  plan: z
    .object({ code: z.string(), name: z.string(), priceMinor: z.number().int(), currency: z.string() })
    .nullable(),
  trialEndsOn: z.iso.date().nullable(),
  currentPeriodEnd: z.iso.date().nullable(),
  attemptsMade: z.number().int(),
  lastFailure: z.string().nullable(),
  restrictedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  /** Последние оплаты — без реквизитов карты: менеджеру нужен факт, а не карта. */
  payments: z.array(AdminPaymentView),
  /** Что клиника принесла этому менеджеру за всё время, пока она за ним. */
  earnings: z.object({
    revenueMinor: z.number().int(),
    commissionMinor: z.number().int(),
  }),
});
export type ManagerTenantDetail = z.infer<typeof ManagerTenantDetail>;

/** Менеджер нажал «Позвонить» или «Написать» — это и пишется в журнал. */
export const ManagerContactEvent = z.object({
  channel: z.enum(['phone', 'email']),
  target: z.string().trim().min(3).max(320),
});
export type ManagerContactEvent = z.infer<typeof ManagerContactEvent>;

// --- Период отчётов ------------------------------------------------------------------
//
// Дни — московские: клиника, менеджер и сотрудник платформы живут по Москве, и
// оплата в 01:00 первого числа — это оплата нового месяца, а не прошлого, как
// вышло бы по UTC. Обе границы включительно: «с 1 по 31 октября».

const MAX_PERIOD_DAYS = 732;

export const ReportPeriodQuery = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
  })
  .refine((value) => value.from <= value.to, { message: 'Начало периода позже конца', path: ['from'] })
  .refine(
    (value) => Date.parse(`${value.to}T00:00:00Z`) - Date.parse(`${value.from}T00:00:00Z`) <= MAX_PERIOD_DAYS * 86_400_000,
    { message: 'Период длиннее двух лет', path: ['to'] },
  );
export type ReportPeriodQuery = z.infer<typeof ReportPeriodQuery>;

export const ReportPeriod = z.object({ from: z.iso.date(), to: z.iso.date() });
export type ReportPeriod = z.infer<typeof ReportPeriod>;

// --- Реферальные ссылки менеджера -------------------------------------------------------
//
// Ссылка — `odonix.ru/r/<код>`: переход считается, и сайт запоминает код до
// регистрации. Клиника, зарегистрированная по ссылке, закрепляется за её
// менеджером сама — без участия сотрудника платформы.

export const REFERRAL_CODE_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

export const ReferralCode = z
  .string()
  .trim()
  .toLowerCase()
  .regex(REFERRAL_CODE_PATTERN, 'Латиница, цифры и дефис, от 3 до 32 знаков, без дефиса по краям');

const ReferralLabel = z.string().trim().max(80, 'Подпись не длиннее 80 знаков');

export const ReferralLinkView = z.object({
  id: Id,
  code: ReferralCode,
  label: z.string().nullable(),
  /** Готовая ссылка для отправки клинике. */
  url: z.string(),
  createdAt: z.iso.datetime(),
  disabledAt: z.iso.datetime().nullable(),
  visits: z.number().int(),
  visits30d: z.number().int(),
  registrations: z.number().int(),
});
export type ReferralLinkView = z.infer<typeof ReferralLinkView>;

export const ReferralLinkListResponse = z.object({
  links: z.array(ReferralLinkView),
});
export type ReferralLinkListResponse = z.infer<typeof ReferralLinkListResponse>;

/** Без кода — сервер придумает его из фамилии менеджера. */
export const CreateReferralLinkRequest = z.object({
  code: ReferralCode.optional(),
  label: ReferralLabel.optional(),
});
export type CreateReferralLinkRequest = z.infer<typeof CreateReferralLinkRequest>;

export const UpdateReferralLinkRequest = z
  .object({
    label: ReferralLabel.nullable().optional(),
    active: z.boolean().optional(),
  })
  .refine((value) => value.label !== undefined || value.active !== undefined, { message: 'Укажите, что изменить' });
export type UpdateReferralLinkRequest = z.infer<typeof UpdateReferralLinkRequest>;

// --- Показатели менеджера ----------------------------------------------------------------
//
// Одни и те же числа видят и сам менеджер в кабинете, и сотрудник платформы в
// карточке менеджера: расхождение «у меня одно, у вас другое» здесь означало
// бы спор о деньгах.

export const ManagerMonthStats = z.object({
  /** Первое число месяца. */
  month: z.iso.date(),
  revenueMinor: z.number().int(),
  commissionMinor: z.number().int(),
  bonusesMinor: z.number().int(),
  registrations: z.number().int(),
  visits: z.number().int(),
});
export type ManagerMonthStats = z.infer<typeof ManagerMonthStats>;

export const ManagerStats = z.object({
  period: ReportPeriod,
  commissionPercent: z.number(),
  /** Клиники за менеджером сейчас — снимок, а не период. */
  tenants: z.object({
    total: z.number().int(),
    paying: z.number().int(),
    trial: z.number().int(),
    /** Ждут оплаты, с закрытой записью или приостановлены. */
    attention: z.number().int(),
    byState: z.record(SubscriptionState, z.number().int()),
  }),
  /** Закреплены за период — по ссылке и сотрудником. */
  newTenants: z.number().int(),
  /** Зарегистрировались по ссылкам менеджера за период. */
  registrations: z.number().int(),
  /** Переходы по ссылкам менеджера за период. */
  visits: z.number().int(),
  /** Оплаты его клиник за период за вычетом возвратов. */
  revenueMinor: z.number().int(),
  commissionMinor: z.number().int(),
  bonusesMinor: z.number().int(),
  /** Процент плюс премии. */
  payoutMinor: z.number().int(),
  activity: z.object({
    calls: z.number().int(),
    emails: z.number().int(),
    views: z.number().int(),
    lastLoginAt: z.iso.datetime().nullable(),
  }),
  /** Двенадцать месяцев, последний — месяц конца периода; от старого к новому. */
  months: z.array(ManagerMonthStats),
});
export type ManagerStats = z.infer<typeof ManagerStats>;

/** Начисление с одной оплаты. */
export const ManagerAccrual = z.object({
  paymentId: Id,
  tenantId: Id,
  tenantName: z.string(),
  kind: z.enum(['initial', 'recurring', 'manual']),
  settledAt: z.iso.datetime(),
  /** Оплата за вычетом возвратов — база процента. */
  amountMinor: z.number().int(),
  percent: z.number(),
  commissionMinor: z.number().int(),
});
export type ManagerAccrual = z.infer<typeof ManagerAccrual>;

export const ManagerBonusView = z.object({
  id: Id,
  amountMinor: z.number().int(),
  /** Первое число месяца, за который премия. */
  month: z.iso.date(),
  comment: z.string(),
  createdAt: z.iso.datetime(),
  createdByEmail: z.string().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelReason: z.string().nullable(),
});
export type ManagerBonusView = z.infer<typeof ManagerBonusView>;

export const ManagerEarningsResponse = z.object({
  period: ReportPeriod,
  accruals: z.array(ManagerAccrual),
  bonuses: z.array(ManagerBonusView),
  totals: z.object({
    revenueMinor: z.number().int(),
    commissionMinor: z.number().int(),
    bonusesMinor: z.number().int(),
    payoutMinor: z.number().int(),
  }),
});
export type ManagerEarningsResponse = z.infer<typeof ManagerEarningsResponse>;

// --- Менеджеры в админке: карточки и действия ---------------------------------------------

export const ManagerOverviewItem = z.object({
  manager: ManagerView,
  tenants: z.number().int(),
  paying: z.number().int(),
  newTenants: z.number().int(),
  registrations: z.number().int(),
  visits: z.number().int(),
  revenueMinor: z.number().int(),
  commissionMinor: z.number().int(),
  bonusesMinor: z.number().int(),
  payoutMinor: z.number().int(),
  calls: z.number().int(),
});
export type ManagerOverviewItem = z.infer<typeof ManagerOverviewItem>;

export const ManagersOverviewResponse = z.object({
  period: ReportPeriod,
  managers: z.array(ManagerOverviewItem),
  /** Клиник ни за кем — кандидаты на закрепление. */
  unassignedTenants: z.number().int(),
});
export type ManagersOverviewResponse = z.infer<typeof ManagersOverviewResponse>;

export const ManagerAssignedTenant = z.object({
  id: Id,
  name: z.string(),
  kind: z.enum(['clinic', 'lab']),
  subscriptionState: SubscriptionState,
  since: z.iso.datetime(),
  source: z.enum(['referral', 'staff']),
  /** За всё время, пока клиника за менеджером. */
  revenueMinor: z.number().int(),
  commissionMinor: z.number().int(),
});
export type ManagerAssignedTenant = z.infer<typeof ManagerAssignedTenant>;

export const ManagerCommissionChange = z.object({
  percent: z.number(),
  effectiveFrom: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  setByEmail: z.string().nullable(),
});
export type ManagerCommissionChange = z.infer<typeof ManagerCommissionChange>;

export const ManagerCardResponse = z.object({
  manager: ManagerView,
  stats: ManagerStats,
  commissionHistory: z.array(ManagerCommissionChange),
  tenants: z.array(ManagerAssignedTenant),
  links: z.array(ReferralLinkView),
  bonuses: z.array(ManagerBonusView),
});
export type ManagerCardResponse = z.infer<typeof ManagerCardResponse>;

/**
 * Новая ставка действует с указанного дня и перекрывает всё, что стояло с
 * этого дня и позже: «10 % с первого октября» значит десять процентов за
 * каждую оплату с первого октября, даже если в середине месяца стояло другое.
 */
export const SetManagerCommissionRequest = z.object({
  percent: CommissionPercent,
  /** Без даты — с сегодняшнего дня. */
  effectiveFrom: z.iso.date().optional(),
});
export type SetManagerCommissionRequest = z.infer<typeof SetManagerCommissionRequest>;

/**
 * Потолок — двадцать миллионов: суммы премий и расходов лежат в колонках
 * `integer` (до 21,4 млн ₽ в копейках), и больше этого запрос ушёл бы в базу
 * и упал там отказом сервера, а не понятной подписью у поля.
 */
const MoneyMinor = z
  .number()
  .int('Сумма в копейках — целое число')
  .positive('Сумма больше нуля')
  .max(20_000_000_00, 'Сумма не больше двадцати миллионов');

const MonthDay = z.iso.date().refine((value) => value.endsWith('-01'), 'Месяц — первое число');

export const CreateManagerBonusRequest = z.object({
  amountMinor: MoneyMinor,
  /** Первое число месяца, за который премия. */
  month: MonthDay,
  comment: z.string().trim().min(3, 'Напишите, за что премия').max(500),
});
export type CreateManagerBonusRequest = z.infer<typeof CreateManagerBonusRequest>;

export const CancelManagerBonusRequest = z.object({
  reason: z.string().trim().min(3, 'Укажите причину отмены').max(500),
});
export type CancelManagerBonusRequest = z.infer<typeof CancelManagerBonusRequest>;

/** Клиники удаляемого менеджера уходят другому или остаются ни за кем. */
export const DeleteManagerRequest = z.object({
  transferToManagerId: Id.nullable(),
});
export type DeleteManagerRequest = z.infer<typeof DeleteManagerRequest>;

export const DeleteManagerResponse = z.object({
  manager: ManagerView,
  movedTenants: z.number().int(),
});
export type DeleteManagerResponse = z.infer<typeof DeleteManagerResponse>;

/** Закрепить за менеджером сразу несколько клиник — из карточки менеджера. */
export const AssignManagerTenantsRequest = z.object({
  tenantIds: z.array(Id).min(1).max(500),
});
export type AssignManagerTenantsRequest = z.infer<typeof AssignManagerTenantsRequest>;

export const AssignManagerTenantsResponse = z.object({
  assigned: z.number().int(),
});
export type AssignManagerTenantsResponse = z.infer<typeof AssignManagerTenantsResponse>;

// --- Деньги платформы ---------------------------------------------------------------------
//
// Поступления — оплаты подписок; уходит — возвраты, расходы, проценты
// менеджерам и их премии. Числа за период и помесячно за год, тем же
// московским календарём, что и показатели менеджеров.

export const FinanceMonth = z.object({
  month: z.iso.date(),
  incomeMinor: z.number().int(),
  refundsMinor: z.number().int(),
  expensesMinor: z.number().int(),
  commissionsMinor: z.number().int(),
  bonusesMinor: z.number().int(),
  netMinor: z.number().int(),
});
export type FinanceMonth = z.infer<typeof FinanceMonth>;

export const FinanceOperation = z.object({
  kind: z.enum(['payment', 'refund', 'expense', 'bonus']),
  id: Id,
  at: z.iso.datetime(),
  title: z.string(),
  /** Со знаком: плюс — пришло, минус — ушло. */
  amountMinor: z.number().int(),
  tenantId: Id.nullable(),
});
export type FinanceOperation = z.infer<typeof FinanceOperation>;

export const FinanceSummaryResponse = z.object({
  period: ReportPeriod,
  incomeMinor: z.number().int(),
  paymentsCount: z.number().int(),
  refundsMinor: z.number().int(),
  expensesMinor: z.number().int(),
  commissionsMinor: z.number().int(),
  bonusesMinor: z.number().int(),
  /** Всё, что ушло: возвраты, расходы, проценты и премии. */
  outgoingMinor: z.number().int(),
  netMinor: z.number().int(),
  expensesByCategory: z.array(z.object({ categoryId: Id, name: z.string(), amountMinor: z.number().int() })),
  managers: z.array(
    z.object({
      managerId: Id,
      fullName: z.string(),
      revenueMinor: z.number().int(),
      commissionMinor: z.number().int(),
      bonusesMinor: z.number().int(),
    }),
  ),
  months: z.array(FinanceMonth),
  recent: z.array(FinanceOperation),
});
export type FinanceSummaryResponse = z.infer<typeof FinanceSummaryResponse>;

// --- Расходы платформы ---------------------------------------------------------------------

const CategoryName = z.string().trim().min(2, 'Название не короче двух букв').max(60, 'Не длиннее 60 знаков');

export const PlatformExpenseCategoryView = z.object({
  id: Id,
  name: z.string(),
  archivedAt: z.iso.datetime().nullable(),
  expensesCount: z.number().int(),
});
export type PlatformExpenseCategoryView = z.infer<typeof PlatformExpenseCategoryView>;

export const PlatformExpenseCategoryListResponse = z.object({
  categories: z.array(PlatformExpenseCategoryView),
});
export type PlatformExpenseCategoryListResponse = z.infer<typeof PlatformExpenseCategoryListResponse>;

export const CreatePlatformExpenseCategoryRequest = z.object({ name: CategoryName });
export type CreatePlatformExpenseCategoryRequest = z.infer<typeof CreatePlatformExpenseCategoryRequest>;

/** Категорию не удаляют, а убирают в архив: старые расходы в ней остаются. */
export const UpdatePlatformExpenseCategoryRequest = z
  .object({ name: CategoryName.optional(), archived: z.boolean().optional() })
  .refine((value) => value.name !== undefined || value.archived !== undefined, { message: 'Укажите, что изменить' });
export type UpdatePlatformExpenseCategoryRequest = z.infer<typeof UpdatePlatformExpenseCategoryRequest>;

export const PlatformExpenseView = z.object({
  id: Id,
  categoryId: Id,
  categoryName: z.string(),
  title: z.string(),
  amountMinor: z.number().int(),
  spentOn: z.iso.date(),
  comment: z.string().nullable(),
  createdAt: z.iso.datetime(),
  createdByEmail: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type PlatformExpenseView = z.infer<typeof PlatformExpenseView>;

export const PlatformExpenseListQuery = z.object({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  categoryId: Id.optional(),
  q: z.string().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type PlatformExpenseListQuery = z.infer<typeof PlatformExpenseListQuery>;

export const PlatformExpenseListResponse = z.object({
  expenses: z.array(PlatformExpenseView),
  nextCursor: z.string().nullable(),
  /** По всему отбору, а не по странице. */
  totals: z.object({
    count: z.number().int(),
    amountMinor: z.number().int(),
    byCategory: z.array(z.object({ categoryId: Id, name: z.string(), amountMinor: z.number().int() })),
  }),
});
export type PlatformExpenseListResponse = z.infer<typeof PlatformExpenseListResponse>;

const ExpenseTitle = z.string().trim().min(2, 'Напишите, на что потрачено').max(200);
const ExpenseComment = z.string().trim().max(1000, 'Комментарий не длиннее 1000 знаков');

export const CreatePlatformExpenseRequest = z.object({
  categoryId: Id,
  title: ExpenseTitle,
  amountMinor: MoneyMinor,
  spentOn: z.iso.date(),
  comment: ExpenseComment.optional(),
});
export type CreatePlatformExpenseRequest = z.infer<typeof CreatePlatformExpenseRequest>;

export const UpdatePlatformExpenseRequest = z
  .object({
    categoryId: Id.optional(),
    title: ExpenseTitle.optional(),
    amountMinor: MoneyMinor.optional(),
    spentOn: z.iso.date().optional(),
    /** Пустая строка стирает комментарий. */
    comment: ExpenseComment.optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), { message: 'Укажите, что изменить' });
export type UpdatePlatformExpenseRequest = z.infer<typeof UpdatePlatformExpenseRequest>;
